// DemandReadinessPanel — Phase 1 strategic empty-state panel.
//
// Renders below the hero when sparse data could read as "nothing here".
// Shows TEAM Connect's full posture (Marketplace ON, Wishlist v2 wired,
// K-anon enforced, Sponsor view deferred, Racket Lab OFF in prod, B3.1
// adoption push live) so demos feel intentional rather than empty.

import React from "react";
import { CheckCircle2, Shield, ChevronRight, Sparkles, Database, Target } from "lucide-react";

const Pill = ({ label, ok = true }) => (
  <div className="flex items-center gap-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-xs text-slate-700">
    {ok ? (
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
    ) : (
      <Shield className="h-3.5 w-3.5 text-slate-400 shrink-0" />
    )}
    <span>{label}</span>
  </div>
);

export const DemandReadinessPanel = ({ t, included = 0 }) => {
  // Phase 2 Sponsor View gate target.
  const target = 25;
  const current = Math.max(0, Number(included) || 0);
  const pct = Math.min(100, Math.round((current / target) * 100));
  return (
    <div
      className="rounded-xl border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/40 to-white p-5 sm:p-6"
      data-testid="demand-readiness-panel"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {t("marketplace.demand.readinessTitle")}
          </div>
          <div className="text-sm text-slate-600 mt-1 max-w-2xl">
            {t("marketplace.demand.readinessSub")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
        <Pill label={t("marketplace.demand.readinessStatusMarketplace")} />
        <Pill label={t("marketplace.demand.readinessStatusWlp")} />
        <Pill label={t("marketplace.demand.readinessStatusKAnon")} />
        <Pill label={t("marketplace.demand.readinessStatusPrivacy")} />
        <Pill label={t("marketplace.demand.readinessStatusRacketLab")} ok={false} />
        <Pill label={t("marketplace.demand.readinessStatusSponsorView")} ok={false} />
      </div>

      <div className="mt-4 rounded-md bg-white border border-emerald-100 p-3 flex items-center gap-3">
        <Database className="h-4 w-4 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0 text-xs text-slate-700">
          <div className="font-medium text-slate-900">
            {t("marketplace.demand.readinessAdoptionLabel")}
          </div>
          <div>{t("marketplace.demand.readinessAdoptionRunning")}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>

      {/* Next milestone footer — communicates roadmap, not limitation.
          Progress bar is a soft visual cue without claiming a hard
          deadline; admins can read it as "we're on the way". */}
      <div
        className="mt-3 rounded-md border border-emerald-100 bg-white p-3"
        data-testid="demand-readiness-milestone"
      >
        <div className="flex items-start gap-3">
          <Target className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-900">
                {t("marketplace.demand.milestoneLabel")}
              </span>
              <span className="text-[11px] font-mono text-slate-600">
                <span data-testid="demand-readiness-milestone-current">{current}</span>
                <span className="text-slate-300"> / </span>
                <span data-testid="demand-readiness-milestone-target">{target}</span>
                <span className="text-slate-400"> · {pct}%</span>
              </span>
            </div>
            <div className="text-xs text-slate-600 mt-1 leading-relaxed">
              {t("marketplace.demand.milestoneBody")}
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-emerald-50 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="demand-readiness-milestone-bar"
                aria-label={`${current} / ${target}`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemandReadinessPanel;
